/**
 * JWT Token Utilities
 * Provides functions for parsing and validating JWT tokens
 */

export interface JwtPayload {
  user: {
    id: string;
    uuid: string;
    org: string;
    [key: string]: any;
  };
  exp: number;
  iat: number;
  [key: string]: any;
}

/**
 * Parse a JWT token and return the decoded payload
 * @param token - The JWT token string
 * @returns The decoded JWT payload
 * @throws Error if the token format is invalid
 */
export const parseJwtToken = (token: string): JwtPayload => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      throw new Error('Invalid token format');
    }
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    throw new Error('Failed to parse JWT token: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
};

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string
 * @returns true if token is expired, false otherwise
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = parseJwtToken(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/**
 * Get the expiration date of a JWT token
 * @param token - The JWT token string
 * @returns The expiration date or null if invalid
 */
export const getTokenExpirationDate = (token: string): Date | null => {
  try {
    const decoded = parseJwtToken(token);
    return new Date(decoded.exp * 1000);
  } catch {
    return null;
  }
};
